import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { isEqual } from "lodash";
import useSWR from "swr";

import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";

import useAuth from "../../ContextProvider/AuthProvider";
import { filterAscDescData, formatPhoneNumber, hexToRgba } from "../../Utils.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import PawDataView from "../Common/PawDataView.js";
import { ReactComponent as EditIcon } from "../../Assets/Images/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../Assets/Images/DeleteIcon.svg";
import EmergencyContactsForm from "../Forms/EmergencyContactsForm.js";
import ApiService from "../../Api/ApiServices.js";
import Layout from "../Side bar/Layout.js";
import { MainPageWrapper } from "./GlobalItemList.js";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

export default function EmergencyContact() {
  const { userId } = useAuth();
  const [filter, setFilter] = useState({});
  const [emergenctData, setEmergenctData] = useState([]);
  const [isShow, setIsShow] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [askConfirmation, setAskConfirmation] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const fetchIdRef = useRef(0);
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    data = [],
    mutate: fetchEmergencyData,
    isLoading,
  } = useSWR(
    userId ? `Emergencydata${userId}` : null,
    async () => {
      const { data } = await ApiService.getAllEmergencyUserId(userId);
      return data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { sort } = filter;
    if (filter && filter.from > -1) {
      const allData = [...data];
      const filteredResult = filterAscDescData(allData, sort);
      setEmergenctData(filteredResult);
    }
  }, [filter, data]);

  const handleView = (item) => {
    setIsShow(true);
    setSelectedData(item);
    setIsAdd(false);
    setIsEdit(false);
  };

  const addOnclick = () => {
    setIsAdd(true);
    setSelectedData({});
    setIsShow(false);
    setIsEdit(false);
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    setSelectedData(item);
    setIsAdd(true);
    setIsShow(false);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: `${t("screens.emergency.firstName")}`,
        textAlign: "center",
        accessor: "emer_fname",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.emergency.lastName")}`,
        textAlign: "center",
        accessor: "emer_lname",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.emergency.email")}`,
        accessor: "emer_email",
        textAlign: "center",
        width: "12.5vw",
      },
      {
        Header: `${t("screens.formData.view")}`,
        accessor: "view",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
              color="info"
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.info.main, 0.10)}`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => handleView(row.original)}
            >
              <ViewIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                  width: "24px",
                  height: "24px",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
      {
        Header: `${t("screens.formData.edit")}`,
        accessor: "edit",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
              color="info"
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.info.main, 0.10)}`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => handleEdit(row.original)}
            >
             <EditIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
      {
        Header: `${t("screens.formData.delete")}`,
        accessor: "delete",
        width: "4vw",
        textAlign: "center",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <Button
             color="info"
              sx={{
                minWidth: "40px",
                background:   `${hexToRgba(theme.palette.info.main, 0.10)}`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => setAskConfirmation(row?.original)}
            >
             <DeleteIcon
                style={{
                  fill: `${theme.palette.info.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);



  const props = {
    label: `${t("screens.profile.emergencyContacts")}`,
    isLoading: isLoading,
    columns,
    columnsData: emergenctData,
    details: " There are no emergency contact details for this user.",
    addOnclick: addOnclick,
    fetchData,
  };

  const handleDelete = async () => {
    try {
      if (askConfirmation) {
        const { data } = await ApiService.deleteEmergency(
          userId,
          askConfirmation
        );
        if (data?.OK) {
          fetchEmergencyData();
        }
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const viewProps = {
    label: `${t("screens.emergency.emergencyContactsInformation")}`,
    askConfirmation,
    setAskConfirmation,
    onDelete: handleDelete,
    isShow,
    detailsData: [
      [
        {
          name: `${t("screens.emergency.firstName")}`,
          value: selectedData?.emer_fname || "_",
        },
        {
          name: `${t("screens.emergency.lastName")}`,
          value: selectedData?.emer_lname || "_",
        },
        {
          name: `${t("screens.emergency.email")}`,
          value: selectedData?.emer_email || "_",
        },
        {
          name: `${t("screens.emergency.otherRelationship")}`,
          value: selectedData?.emer_other_relation || "_",
        },
        {
          name: `${t("screens.emergency.relationship")}`,
          value: `${selectedData?.emer_relation || "_"}`,
        },
        {
          name: `${t("screens.emergency.secondaryPhone")}`,
          value: selectedData?.emer_secondaryphone
            ? formatPhoneNumber(selectedData?.emer_secondaryphone)
            : "_",
        },
        {
          name: `${t("screens.emergency.primaryPhone")}`,
          value: selectedData?.emer_primaryphone
            ? formatPhoneNumber(selectedData?.emer_primaryphone)
            : "_",
        },
      ],
      [
        {
          name: `${t("screens.emergency.streetAddress")}`,
          value: selectedData?.emer_address1 || "_",
        },
        {
          name: `${t("screens.emergency.address2")}`,
          value: selectedData?.emer_address2 || "_",
        },
        {
          name: `${t("screens.emergency.city")}`,
          value: selectedData?.emer_city || "_",
        },
        {
          name: `${t("screens.emergency.state")}`,
          value: selectedData?.emer_state || "_",
        },
        {
          name: `${t("screens.emergency.zipCode")}`,
          value: selectedData?.emer_zipcode || "_",
        },
        {
          name: `${t("screens.emergency.country")}`,
          value: selectedData?.emer_country || "_",
        },
      ],
    ],
  };

  return (
    <Layout>
      <MainPageWrapper>
        <PawItemList {...props} />
        <PawDataView {...viewProps} />
        {isAdd && (
          <EmergencyContactsForm
            setIsAdd={setIsAdd}
            isEdit={isEdit}
            data={selectedData}
            fetchEmergencyData={fetchEmergencyData}
          />
        )}
      </MainPageWrapper>
    </Layout>
  );
}
